/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1733356800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Thu Dec 05 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "adikteev",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Adikteev",
          "depth": 0,
          "id": "connect-to-adikteev"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing custom audiences",
          "depth": 1,
          "id": "syncing-custom-audiences"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      img: "img",
      h3: "h3",
      h4: "h4"
    }, _provideComponents(), props.components), {CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "With Hightouch and the Adikteev API, you can send data from your warehouse to audiences in Adikteev.\nDeliver relevant ads by utilizing data from various sources within your data warehouse."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Create audience lists and keep them up-to-date in Adikteev."
            }), _jsx(_components.td, {
              children: "All (file upload)"
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-adikteev",
        children: "Connect to Adikteev"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Adikteev"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to Adikteev via username and password."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-adikteev-config.png",
          alt: "Authenticating to Adikteev in Hightouch"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Enter your Adikteev credentials and hit ", _jsx(_components.strong, {
          children: "Test Connection"
        }), " to verify credentials."]
      }), "\n", _jsxs(_components.p, {
        children: ["If successful, click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". To finish connecting, give your Adikteev destination a descriptive name."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've connected your Adikteev destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Adikteev destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-custom-audiences",
        children: "Syncing custom audiences"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you create and maintain custom audiences via the ", _jsx(_components.a, {
          href: "https://help.adikteev.com/hc/en-us/articles/360003819660-Custom-Audiences-API",
          children: "Custom Audiences API"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "user-identifiers",
        children: "User identifiers"
      }), "\n", _jsx(_components.p, {
        children: "To identify which users to add or update in an audience, select the model column that represents the user's device ID."
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Adikteev allows two different device ID types for iOS devices: IDFA and IDFV.\nBe sure to select the correct option in the ID type dropdown."
        })
      }), "\n", _jsx(_components.h4, {
        id: "custom-audiences-api-mode",
        children: "Custom Audiences API mode"
      }), "\n", _jsxs(_components.p, {
        children: ["Adikteev lets you upload an entire audience through their ", _jsx(_components.a, {
          href: "https://help.adikteev.com/hc/en-us/articles/360003819660-Custom-Audiences-API#SendingCustomAudiencesToAdikteev-Step3-Uploading/UpdatingadeviceIDlistforasegment",
          children: "Custom Audiences API"
        }), ". Hightouch uploads the entire model query results for each sync run.\nThese results effectively replaces the previous upload."]
      }), "\n", _jsx(_components.h4, {
        id: "custom-audience-list",
        children: "Custom audience list"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch lets you use an existing audience in Adikteev or create a new one. Note that if you create a new audience, you can't\nchange the name once your sync has run. Instead, you have to create a new sync."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
